import { ICON_PROPS, formatRelative, friendlyAiError, postToHost, shortDate, tierColor } from './shared.js';
import { useEffect, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  Code,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  Group,
  List,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  NavLink,
  NumberInput,
  Paper,
  PasswordInput,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArchive,
  IconArrowBackUp,
  IconBell,
  IconBolt,
  IconBrain,
  IconBriefcase,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCommand,
  IconDeviceMobile,
  IconDownload,
  IconEdit,
  IconFileText,
  IconFlag,
  IconFolder,
  IconGavel,
  IconHistory,
  IconMail,
  IconMailOff,
  IconMessageQuestion,
  IconMoonStars,
  IconNews,
  IconPaperclip,
  IconPencil,
  IconPhone,
  IconPlus,
  IconRadar,
  IconReload,
  IconReport,
  IconSchool,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShield,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconTarget,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconUsersGroup,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import { api } from '../../api.js';
// Round 48 follow-up (2026-07-15): AddinPage.tsx monolith split — ask panel + discover sections,
// moved VERBATIM. Layering: shared ← shared-components ← panels ← AddinPage.

export function AskPanel(): React.JSX.Element {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.addinAsk>> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Evidence-first: which source the user is hovering / has pinned.
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  async function ask(question?: string) {
    const text = (question ?? q).trim();
    if (text.length < 3) return;
    if (question) setQ(text);
    setBusy(true);
    setErr(null);
    // Round 38 hotfix (2026-06-12): also clear the previous result so a
    // failing query doesn't leave the user with both a red error banner
    // AND a stale answer to a different question. Richard's screenshot
    // showed "AI request failed" sitting above a long answer about
    // Lamis — the answer was from a prior query, the error was from a
    // subsequent one that failed at the fetch layer. Visually confusing.
    setResult(null);
    try {
      setResult(await api.addinAsk(text));
    } catch (e) {
      setErr(friendlyAiError(e));
    } finally {
      setBusy(false);
    }
  }

  // Listen for header search "Ask AI" submissions and auto-fire.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (typeof detail === 'string' && detail.length >= 3) ask(detail);
    };
    window.addEventListener('ai-outlook:ask-prefill', handler);
    return () => window.removeEventListener('ai-outlook:ask-prefill', handler);
    // biome-ignore lint/correctness/useExhaustiveDependencies: ask is stable
  }, []);

  return (
    <Stack gap="md">
      <Stack gap={0}>
        <Title order={4}>Ask your mailbox</Title>
        <Text size="xs" c="dimmed">
          Natural-language Q&A grounded in your indexed emails. Cited inline.
        </Text>
      </Stack>

      <Card padding="md" radius="md" withBorder>
        <Stack gap="xs">
          <Textarea
            placeholder="e.g. What did Hassan say about the renewal pricing?"
            autosize
            minRows={2}
            maxRows={4}
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                ask();
              }
            }}
          />
          <Group justify="space-between">
            <Text size="xs" c="dimmed">⌘+Enter to ask</Text>
            <Button size="xs" leftSection={<IconSparkles {...ICON_PROPS} />} loading={busy} onClick={() => ask()} disabled={q.trim().length < 3}>
              Ask
            </Button>
          </Group>
        </Stack>
      </Card>

      {err && (
        <Alert color="red" variant="light">
          {err}
        </Alert>
      )}

      {result && (
        <Card padding="md" radius="md" withBorder>
          <Stack gap="sm">
            {/* Answer with clickable [N] citation chips. Hovering / clicking
                a chip pins the matching source's quote pull-out below. */}
            <AnswerWithCitations
              text={result.answer}
              cited={new Set(result.cited_indices)}
              activeIdx={activeIdx}
              onCite={(n) => setActiveIdx((prev) => (prev === n ? null : n))}
            />

            {result.sources.length > 0 && (
              <>
                <Divider
                  label={
                    <Group gap={4}>
                      <Text size="xs">Sources</Text>
                      <Badge size="xs" color="green" variant="light">
                        {result.cited_indices.length} cited
                      </Badge>
                      {result.sources.length - result.cited_indices.length > 0 && (
                        <Badge size="xs" color="gray" variant="light">
                          {result.sources.length - result.cited_indices.length} unused
                        </Badge>
                      )}
                    </Group>
                  }
                  labelPosition="left"
                />
                <Stack gap={4}>
                  {result.sources.map((s) => {
                    const isActive = activeIdx === s.index;
                    const dimmed = !s.cited && activeIdx !== s.index;
                    return (
                      <Paper
                        key={s.message_id}
                        p="xs"
                        withBorder
                        style={{
                          borderLeft: `3px solid var(--mantine-color-${
                            isActive ? 'blue' : s.cited ? 'green' : 'gray'
                          }-5)`,
                          opacity: dimmed ? 0.55 : 1,
                          cursor: 'pointer',
                        }}
                        onClick={() => setActiveIdx((prev) => (prev === s.index ? null : s.index))}
                      >
                        <Group gap="xs" wrap="nowrap" align="flex-start">
                          <Badge
                            size="sm"
                            variant={s.cited ? 'filled' : 'light'}
                            color={isActive ? 'blue' : s.cited ? 'green' : 'gray'}
                          >
                            [{s.index}]
                          </Badge>
                          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                            <Group gap={4} wrap="nowrap" justify="space-between">
                              <Text size="sm" fw={500} lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
                                {s.subject ?? '(no subject)'}
                              </Text>
                              <Tooltip label="Open in Outlook">
                                <ActionIcon
                                  size="sm"
                                  variant="subtle"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    postToHost({ type: 'open_in_outlook', message_id: s.message_id });
                                  }}
                                >
                                  <IconMail {...ICON_PROPS} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {s.from_addr} · {shortDate(s.received_at)}
                            </Text>
                            {(isActive || s.cited) && s.snippet && (
                              <Paper
                                p={6}
                                radius="xs"
                                style={{
                                  background: 'var(--mantine-color-gray-0)',
                                  borderLeft: '2px solid var(--mantine-color-gray-4)',
                                }}
                              >
                                <Text size="xs" style={{ whiteSpace: 'pre-wrap' }} {...(isActive ? {} : { lineClamp: 3 })}>
                                  {s.snippet}
                                </Text>
                              </Paper>
                            )}
                          </Stack>
                        </Group>
                      </Paper>
                    );
                  })}
                </Stack>
              </>
            )}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

// Renders Ask-AI answer text with [N] tokens converted to clickable badges.
// Pinned sources highlight on the active citation; unused citations stay dim.
export function AnswerWithCitations({
  text,
  cited,
  activeIdx,
  onCite,
}: {
  text: string;
  cited: Set<number>;
  activeIdx: number | null;
  onCite: (n: number) => void;
}): React.JSX.Element {
  // Split on [N] tokens, preserving the matched groups for inline rendering.
  const parts: Array<{ kind: 'text'; value: string } | { kind: 'cite'; n: number }> = [];
  const re = /\[(\d{1,2})\]/g;
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push({ kind: 'text', value: text.slice(lastIdx, m.index) });
    parts.push({ kind: 'cite', n: Number.parseInt(m[1]!, 10) });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push({ kind: 'text', value: text.slice(lastIdx) });

  return (
    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
      {parts.map((p, i) => {
        if (p.kind === 'text') return <span key={i}>{p.value}</span>;
        const isCited = cited.has(p.n);
        const isActive = activeIdx === p.n;
        return (
          <Badge
            key={i}
            size="xs"
            variant={isActive ? 'filled' : isCited ? 'light' : 'outline'}
            color={isActive ? 'blue' : isCited ? 'green' : 'gray'}
            style={{ cursor: 'pointer', margin: '0 2px', verticalAlign: 'middle' }}
            onClick={(e) => { e.stopPropagation(); onCite(p.n); }}
          >
            [{p.n}]
          </Badge>
        );
      })}
    </Text>
  );
}

// =========================================================================
// Discover panel — collection of insight features. Round 40.
// Tabs inside: Decisions, Contradictions, Important-not-urgent, Resolved,
// Unsubscribe, Noise budget, Anti-VIPs, SLA, Status report.
// =========================================================================

export function DiscoverPanel(): React.JSX.Element {
  const [group, setGroup] = useState<string>('intelligence');
  const [section, setSection] = useState<string>('decisions');

  // Tabs grouped by mental category. Each group keeps its own default section
  // so switching groups doesn't dump the user on a blank panel.
  const groups: Record<string, { label: string; icon: React.JSX.Element; sections: Array<{ value: string; label: string; icon: React.JSX.Element }> }> = {
    intelligence: {
      label: 'Intelligence',
      icon: <IconBrain {...ICON_PROPS} />,
      sections: [
        { value: 'decisions', label: 'Decisions', icon: <IconGavel {...ICON_PROPS} /> },
        { value: 'contradictions', label: 'Contradictions', icon: <IconAlertTriangle {...ICON_PROPS} /> },
        { value: 'map', label: 'Mailbox map', icon: <IconRadar {...ICON_PROPS} /> },
        { value: 'versions', label: 'File versions', icon: <IconPaperclip {...ICON_PROPS} /> },
        { value: 'training', label: 'Training', icon: <IconSchool {...ICON_PROPS} /> },
      ],
    },
    queues: {
      label: 'Queues',
      icon: <IconTarget {...ICON_PROPS} />,
      sections: [
        { value: 'important', label: 'Important / not urgent', icon: <IconStar {...ICON_PROPS} /> },
        { value: 'sla', label: 'SLA breaches', icon: <IconClock {...ICON_PROPS} /> },
        { value: 'resolved', label: 'Resolved threads', icon: <IconCheck {...ICON_PROPS} /> },
      ],
    },
    cleanup: {
      label: 'Cleanup',
      icon: <IconArchive {...ICON_PROPS} />,
      sections: [
        { value: 'noisekiller', label: 'Noise Killer ★', icon: <IconBolt {...ICON_PROPS} /> },
        { value: 'unsubscribe', label: 'Unsubscribe', icon: <IconMailOff {...ICON_PROPS} /> },
        { value: 'noise', label: 'Noise budget', icon: <IconChartBar {...ICON_PROPS} /> },
        { value: 'antivip', label: 'Anti-VIPs', icon: <IconArchive {...ICON_PROPS} /> },
      ],
    },
    automation: {
      label: 'Automation',
      icon: <IconWand {...ICON_PROPS} />,
      sections: [
        { value: 'automations', label: 'Rules from English', icon: <IconWand {...ICON_PROPS} /> },
        { value: 'personas', label: 'Personas', icon: <IconUsersGroup {...ICON_PROPS} /> },
        { value: 'dnd', label: 'Quiet hours', icon: <IconMoonStars {...ICON_PROPS} /> },
        { value: 'report', label: 'Weekly report', icon: <IconReport {...ICON_PROPS} /> },
      ],
    },
    organize: {
      label: 'Organize',
      icon: <IconFolder {...ICON_PROPS} />,
      sections: [
        { value: 'categories', label: 'Categories', icon: <IconFolder {...ICON_PROPS} /> },
      ],
    },
  };
  const currentGroup = groups[group] ?? groups.intelligence;
  const groupSectionValues = (currentGroup?.sections ?? []).map((s) => s.value);
  // If we switched groups, reset section to first in the group.
  useEffect(() => {
    if (currentGroup && !groupSectionValues.includes(section)) {
      const first = currentGroup.sections[0];
      if (first) setSection(first.value);
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: only fire on group change
  }, [group]);

  return (
    <Stack gap="md">
      <Stack gap={0}>
        <Title order={4}>Discover</Title>
        <Text size="xs" c="dimmed">
          Aggregated insights — decisions, contradictions, SLA breaches, automation, and what to clean up.
        </Text>
      </Stack>
      <SegmentedControl
        value={group}
        onChange={setGroup}
        data={Object.entries(groups).map(([k, g]) => ({ value: k, label: g.label }))}
        fullWidth
      />
      <Tabs value={section} onChange={(v) => v && setSection(v)} variant="pills">
        <Tabs.List>
          {(currentGroup?.sections ?? []).map((s) => (
            <Tabs.Tab key={s.value} value={s.value} leftSection={s.icon}>
              {s.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        <Box pt="md">
          {section === 'decisions' && <DecisionsSection />}
          {section === 'contradictions' && <ContradictionsSection />}
          {section === 'important' && <ImportantNotUrgentSection />}
          {section === 'sla' && <SlaSection />}
          {section === 'resolved' && <ResolvedSection />}
          {section === 'unsubscribe' && <UnsubscribeSection />}
          {section === 'noise' && <NoiseBudgetSection />}
          {section === 'noisekiller' && <NoiseKillerSection />}
          {section === 'antivip' && <AntiVipSection />}
          {section === 'report' && <StatusReportSection />}
          {section === 'training' && <TrainingSection />}
          {section === 'personas' && <PersonasSection />}
          {section === 'dnd' && <DndSection />}
          {section === 'map' && <MailboxMapSection />}
          {section === 'versions' && <AttachmentVersionsSection />}
          {section === 'automations' && <AutomationsSection />}
          {section === 'categories' && <CategoriesSection />}
        </Box>
      </Tabs>
    </Stack>
  );
}

// =========================================================================
// Categories (Round 43) — user-defined buckets like Legal, Operators, India
// team, Lebanon team. Each renders as a collapsible Accordion item with the
// email count as summary, expanded list, and quick edit/apply.
// =========================================================================

export function CategoriesSection(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinCategoriesList>> | null>(null);
  const [suggestions, setSuggestions] = useState<Awaited<ReturnType<typeof api.addinCategoryAutoSuggest>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<NonNullable<typeof data>['categories'][number] | null>(null);

  async function load() {
    setBusy(true);
    try {
      const [c, s] = await Promise.all([api.addinCategoriesList(), api.addinCategoryAutoSuggest()]);
      setData(c);
      setSuggestions(s);
    } finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []);

  async function applySuggestion(s: Awaited<ReturnType<typeof api.addinCategoryAutoSuggest>>['suggestions'][number]) {
    try {
      const created = await api.addinCategoryCreate({
        name: s.suggested_name,
        description: s.reason,
        match_senders: s.senders,
      });
      // Apply to all matching messages immediately
      let applied = 0;
      for (const sender of s.senders.slice(0, 20)) {
        const r = await api.addinCategoryApply(created.id, { sender_contains: sender, days: 365 });
        applied += r.applied;
      }
      notifications.show({ message: `Created "${created.name}" and tagged ${applied} email${applied === 1 ? '' : 's'}.` });
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
    }
  }

  async function deleteCategory(id: string, name: string) {
    if (!confirm(`Delete category "${name}"? Emails won't be deleted, just untagged.`)) return;
    try {
      await api.addinCategoryDelete(id);
      load();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="xs" c="dimmed">
          Group emails into your own categories — Legal, Operators (each one separate), India team, Lebanon team, Internal,
          Monitoring, Hosting, etc. Each category has match rules; emails matching the rules get tagged automatically.
        </Text>
        <Group gap="xs">
          <Button size="xs" leftSection={<IconPlus {...ICON_PROPS} />} onClick={() => { setEditing(null); setEditorOpen(true); }}>
            New category
          </Button>
          <Button size="xs" variant="default" loading={busy} onClick={load}>Refresh</Button>
        </Group>
      </Group>

      {suggestions && suggestions.suggestions.length > 0 && (
        <Card padding="md" radius="md" withBorder style={{ borderLeft: '4px solid var(--mantine-color-violet-6)' }}>
          <Group gap="xs" mb="xs">
            <IconSparkles {...ICON_PROPS} />
            <Text fw={600}>Suggested from your mailbox</Text>
            <Badge color="violet">{suggestions.suggestions.length}</Badge>
          </Group>
          <Text size="xs" c="dimmed" mb="xs">
            Heuristic groups detected from your top senders (last 60 days). Click "Create" to add the category and auto-tag matching emails.
          </Text>
          <Stack gap="xs">
            {suggestions.suggestions.map((s) => (
              <Paper key={s.suggested_name} p="xs" withBorder>
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500}>{s.suggested_name}</Text>
                    <Text size="xs" c="dimmed">
                      {s.reason} · {s.senders.length} senders · {s.volume} emails
                    </Text>
                  </Stack>
                  <Button size="compact-xs" onClick={() => applySuggestion(s)}>
                    Create + tag
                  </Button>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Card>
      )}

      {busy && !data ? <Center p="lg"><Loader /></Center> : null}

      {data && data.categories.length === 0 && !busy && (
        <Alert color="gray" variant="light">
          No categories yet. Click "New category" or use a suggestion above.
        </Alert>
      )}

      {data && data.categories.length > 0 && (
        <Accordion multiple variant="separated">
          {data.categories.map((c) => (
            <Accordion.Item key={c.id} value={c.id}>
              <Accordion.Control>
                <Group justify="space-between" wrap="nowrap" gap="xs" pr="md">
                  <Group gap="xs">
                    <IconFolder {...ICON_PROPS} color={c.color || undefined} />
                    <Text fw={600}>{c.name}</Text>
                    <Badge size="sm" color={c.color || 'blue'}>{c.email_count}</Badge>
                  </Group>
                  {c.description && (
                    <Text size="xs" c="dimmed" lineClamp={1} style={{ flex: 1, textAlign: 'right' }}>
                      {c.description}
                    </Text>
                  )}
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <CategoryDetail
                  category={c}
                  onEdit={() => { setEditing(c); setEditorOpen(true); }}
                  onDelete={() => deleteCategory(c.id, c.name)}
                />
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      <CategoryEditorModal
        opened={editorOpen}
        editing={editing}
        onClose={() => { setEditorOpen(false); setEditing(null); }}
        onSaved={() => { setEditorOpen(false); setEditing(null); load(); }}
      />
    </Stack>
  );
}

export function CategoryDetail({
  category,
  onEdit,
  onDelete,
}: {
  category: NonNullable<Awaited<ReturnType<typeof api.addinCategoriesList>>>['categories'][number];
  onEdit: () => void;
  onDelete: () => void;
}): React.JSX.Element {
  const [emails, setEmails] = useState<Awaited<ReturnType<typeof api.addinCategoryMessages>> | null>(null);
  const [busy, setBusy] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: load when category changes
  useEffect(() => {
    setBusy(true);
    api.addinCategoryMessages(category.id, 50).then(setEmails).finally(() => setBusy(false));
  }, [category.id]);

  let senders: string[] = []; let domains: string[] = [];
  let subjectKw: string[] = []; let bodyKw: string[] = [];
  try { senders = JSON.parse(category.match_senders_json) as string[]; } catch { /* ignore */ }
  try { domains = JSON.parse(category.match_domains_json) as string[]; } catch { /* ignore */ }
  try { subjectKw = JSON.parse(category.match_subject_keywords_json) as string[]; } catch { /* ignore */ }
  try { bodyKw = JSON.parse(category.match_body_keywords_json) as string[]; } catch { /* ignore */ }

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <Button size="compact-xs" variant="default" onClick={onEdit}>Edit rules</Button>
        <Button size="compact-xs" variant="subtle" color="red" onClick={onDelete}>Delete</Button>
      </Group>
      {(senders.length + domains.length + subjectKw.length + bodyKw.length) > 0 && (
        <Group gap={4}>
          {senders.map((s) => <Badge key={`s-${s}`} size="xs" variant="light">sender: {s}</Badge>)}
          {domains.map((d) => <Badge key={`d-${d}`} size="xs" variant="light" color="cyan">domain: {d}</Badge>)}
          {subjectKw.map((k) => <Badge key={`sk-${k}`} size="xs" variant="light" color="violet">subj: {k}</Badge>)}
          {bodyKw.map((k) => <Badge key={`bk-${k}`} size="xs" variant="light" color="grape">body: {k}</Badge>)}
        </Group>
      )}
      <Text size="xs" c="dimmed">{category.email_count} emails · {emails?.messages.length ?? 0} shown</Text>
      {busy && !emails ? <Center p="md"><Loader size="sm" /></Center> : null}
      <Stack gap={2}>
        {(emails?.messages ?? []).slice(0, 25).map((m) => (
          <Paper key={m.message_id} p="xs" style={{ cursor: 'pointer' }}
            onClick={() => postToHost({ type: 'open_in_outlook', message_id: m.message_id })}>
            <Group justify="space-between" wrap="nowrap">
              <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={500} lineClamp={1}>{m.subject ?? '(no subject)'}</Text>
                <Text size="xs" c="dimmed" lineClamp={1}>{m.from_addr} · {shortDate(m.received_at)}</Text>
              </Stack>
              {m.classification_tier && <Badge size="xs" color={tierColor(m.classification_tier)}>{m.classification_tier}</Badge>}
            </Group>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
}

export function CategoryEditorModal({
  opened,
  editing,
  onClose,
  onSaved,
}: {
  opened: boolean;
  editing: NonNullable<Awaited<ReturnType<typeof api.addinCategoriesList>>>['categories'][number] | null;
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [senders, setSenders] = useState('');
  const [domains, setDomains] = useState('');
  const [subjectKw, setSubjectKw] = useState('');
  const [bodyKw, setBodyKw] = useState('');
  const [color, setColor] = useState('blue');
  const [autoApply, setAutoApply] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setName(editing.name);
      setDescription(editing.description ?? '');
      setColor(editing.color ?? 'blue');
      setAutoApply(editing.auto_apply === 1);
      try { setSenders((JSON.parse(editing.match_senders_json) as string[]).join('\n')); } catch { setSenders(''); }
      try { setDomains((JSON.parse(editing.match_domains_json) as string[]).join('\n')); } catch { setDomains(''); }
      try { setSubjectKw((JSON.parse(editing.match_subject_keywords_json) as string[]).join('\n')); } catch { setSubjectKw(''); }
      try { setBodyKw((JSON.parse(editing.match_body_keywords_json) as string[]).join('\n')); } catch { setBodyKw(''); }
    } else if (opened) {
      setName(''); setDescription(''); setColor('blue'); setAutoApply(true);
      setSenders(''); setDomains(''); setSubjectKw(''); setBodyKw('');
    }
  }, [editing, opened]);

  function splitLines(s: string): string[] {
    return s.split(/[\n,;]/).map((x) => x.trim()).filter((x) => x.length > 0);
  }

  async function save() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        color,
        match_senders: splitLines(senders),
        match_domains: splitLines(domains),
        match_subject_keywords: splitLines(subjectKw),
        match_body_keywords: splitLines(bodyKw),
        auto_apply: autoApply,
      };
      if (editing) {
        await api.addinCategoryPatch(editing.id, body);
      } else {
        const created = await api.addinCategoryCreate(body);
        // Apply rules immediately so the category isn't empty
        if (body.match_senders.length > 0 || body.match_domains.length > 0 ||
            body.match_subject_keywords.length > 0 || body.match_body_keywords.length > 0) {
          for (const s of body.match_senders.slice(0, 20)) {
            await api.addinCategoryApply(created.id, { sender_contains: s, days: 365 });
          }
          for (const d of body.match_domains.slice(0, 20)) {
            await api.addinCategoryApply(created.id, { sender_contains: '@' + d.replace(/^[*@]+/, ''), days: 365 });
          }
          for (const k of body.match_subject_keywords.slice(0, 20)) {
            await api.addinCategoryApply(created.id, { subject_contains: k, days: 365 });
          }
        }
      }
      notifications.show({ message: editing ? 'Category updated.' : 'Category created and applied.' });
      onSaved();
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
    } finally { setSaving(false); }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={editing ? `Edit "${editing.name}"` : 'New category'} size="lg">
      <Stack gap="sm">
        <Group gap="xs">
          <TextInput label="Name" placeholder='e.g. "Operators · Vodafone"' value={name} onChange={(e) => setName(e.currentTarget.value)} style={{ flex: 1 }} required />
          <Select
            label="Color"
            w={120}
            data={['red', 'orange', 'yellow', 'green', 'teal', 'blue', 'cyan', 'indigo', 'violet', 'grape', 'pink', 'gray']}
            value={color}
            onChange={(v) => v && setColor(v)}
          />
        </Group>
        <TextInput label="Description (optional)" placeholder="What goes in this category and why" value={description} onChange={(e) => setDescription(e.currentTarget.value)} />
        <SimpleGrid cols={2}>
          <Textarea
            label="Match senders (one per line)"
            description="Substring match on from-address. e.g. ahmad@vodafone, support@orange"
            autosize minRows={3} maxRows={6}
            value={senders}
            onChange={(e) => setSenders(e.currentTarget.value)}
            styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
          />
          <Textarea
            label="Match domains (one per line)"
            description="Pattern matched in from-address. e.g. vodafone, orange, ziho"
            autosize minRows={3} maxRows={6}
            value={domains}
            onChange={(e) => setDomains(e.currentTarget.value)}
            styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
          />
          <Textarea
            label="Subject keywords (one per line)"
            autosize minRows={3} maxRows={6}
            value={subjectKw}
            onChange={(e) => setSubjectKw(e.currentTarget.value)}
            styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
          />
          <Textarea
            label="Body keywords (one per line)"
            autosize minRows={3} maxRows={6}
            value={bodyKw}
            onChange={(e) => setBodyKw(e.currentTarget.value)}
            styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
          />
        </SimpleGrid>
        <Switch
          checked={autoApply}
          onChange={(e) => setAutoApply(e.currentTarget.checked)}
          label="Auto-tag new matching emails as they're indexed"
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" size="xs" onClick={onClose}>Cancel</Button>
          <Button size="xs" loading={saving} disabled={!name.trim()} onClick={save}>
            {editing ? 'Save changes' : 'Create + tag matches'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function MailboxMapSection(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinMailboxMap>> | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setBusy(true);
    api.addinMailboxMap().then(setData).finally(() => setBusy(false));
  }, []);
  if (busy && !data) return <Center p="lg"><Loader /></Center>;
  if (!data) return <Alert color="gray" variant="light">No data yet. Index your mailboxes first.</Alert>;
  const maxDomainVolume = Math.max(...data.domains.map((d) => d.volume), 1);
  const maxSenderVolume = Math.max(...data.senders.map((s) => s.volume), 1);
  return (
    <Stack gap="md">
      <Text size="xs" c="dimmed">
        Visual map of who emails you (last 30 days). Domains grouped, top senders sized by volume, cross-domain bridges shown as edges.
      </Text>
      <Card padding="md" radius="md" withBorder>
        <Title order={6} mb="sm">Top domains</Title>
        <Stack gap={6}>
          {data.domains.map((d) => (
            <Group key={d.domain} gap="sm" wrap="nowrap">
              <Text size="xs" w={180} lineClamp={1}>{d.domain}</Text>
              <Box style={{ flex: 1, height: 14, position: 'relative', background: 'var(--mantine-color-default-hover)', borderRadius: 4 }}>
                <Box
                  style={{
                    width: `${(d.volume / maxDomainVolume) * 100}%`,
                    height: '100%',
                    background: 'var(--mantine-color-blue-6)',
                    borderRadius: 4,
                  }}
                />
              </Box>
              <Text size="xs" w={80} ta="right">{d.volume} ({d.senders}p)</Text>
            </Group>
          ))}
        </Stack>
      </Card>
      <Card padding="md" radius="md" withBorder>
        <Title order={6} mb="sm">Top senders</Title>
        <Stack gap={4}>
          {data.senders.slice(0, 20).map((s) => (
            <Group key={s.from_addr} gap="sm" wrap="nowrap">
              <Text size="xs" style={{ flex: 1, minWidth: 0 }} lineClamp={1}>{s.from_addr}</Text>
              <Box style={{ width: 200, height: 12, position: 'relative', background: 'var(--mantine-color-default-hover)', borderRadius: 3 }}>
                <Box
                  style={{
                    width: `${(s.volume / maxSenderVolume) * 100}%`,
                    height: '100%',
                    background: s.top_tier === 'P0' ? 'var(--mantine-color-red-6)' : s.top_tier === 'P1' ? 'var(--mantine-color-orange-6)' : 'var(--mantine-color-gray-6)',
                    borderRadius: 3,
                  }}
                />
              </Box>
              <Text size="xs" w={50} ta="right">{s.volume}</Text>
            </Group>
          ))}
        </Stack>
      </Card>
      {data.edges.length > 0 && (
        <Card padding="md" radius="md" withBorder>
          <Title order={6} mb="sm">Cross-domain bridges</Title>
          <Text size="xs" c="dimmed" mb="xs">Domains that share threads — collaborators, vendors, or compromised loops.</Text>
          <Stack gap={2}>
            {data.edges.slice(0, 15).map((e, i) => (
              <Group key={`${e.source}-${e.target}-${i}`} gap="xs" wrap="nowrap">
                <Text size="xs" style={{ flex: 1 }} ta="right" lineClamp={1}>{e.source}</Text>
                <Text size="xs" c="dimmed">↔</Text>
                <Text size="xs" style={{ flex: 1 }} lineClamp={1}>{e.target}</Text>
                <Badge size="xs" variant="light">{e.shared_threads}</Badge>
              </Group>
            ))}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

export function AttachmentVersionsSection(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinAttachmentVersionsList>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [openFile, setOpenFile] = useState<string | null>(null);
  const [versions, setVersions] = useState<Awaited<ReturnType<typeof api.addinAttachmentVersionsFor>> | null>(null);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [textFilter, setTextFilter] = useState('');

  useEffect(() => {
    setBusy(true);
    api.addinAttachmentVersionsList().then(setData).finally(() => setBusy(false));
  }, []);

  async function openVersions(filename: string) {
    setOpenFile(filename);
    setLoadingVersions(true);
    setVersions(null);
    try {
      setVersions(await api.addinAttachmentVersionsFor(filename));
    } finally { setLoadingVersions(false); }
  }

  if (busy && !data) return <Center p="lg"><Loader /></Center>;
  if (!data || data.items.length === 0) return <Alert color="gray" variant="light">No multi-version files detected. As you receive different versions of the same filename, they'll appear here.</Alert>;
  const lower = textFilter.trim().toLowerCase();
  const visible = lower ? data.items.filter((f) => f.filename.toLowerCase().includes(lower)) : data.items;
  return (
    <Stack gap="md">
      <Text size="xs" c="dimmed">
        Files where multiple distinct versions exist (different content hashes, same filename). Click to walk the version chain.
      </Text>
      <TextInput
        size="xs"
        placeholder="Filter by filename…"
        value={textFilter}
        onChange={(e) => setTextFilter(e.currentTarget.value)}
        rightSection={textFilter ? (
          <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setTextFilter('')}>
            <IconX size={10} />
          </ActionIcon>
        ) : null}
      />
      {visible.length === 0 && lower && (
        <Text size="xs" c="dimmed" ta="center" py="md">No files match this filter.</Text>
      )}
      <Stack gap="xs">
        {visible.map((f) => (
          <Paper key={f.filename} p="sm" withBorder style={{ cursor: 'pointer' }} onClick={() => openVersions(f.filename)}>
            <Group justify="space-between" wrap="nowrap">
              <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                <Group gap={4}>
                  <IconFileText {...ICON_PROPS} />
                  <Text size="sm" fw={500} lineClamp={1}>{f.filename}</Text>
                </Group>
                <Text size="xs" c="dimmed">
                  {f.distinct_versions} versions across {f.total_copies} emails · last seen {shortDate(f.last_seen)}
                </Text>
              </Stack>
              <Badge color="teal">v{f.distinct_versions}</Badge>
            </Group>
          </Paper>
        ))}
      </Stack>
      <Drawer opened={openFile !== null} onClose={() => setOpenFile(null)} position="right" size="md" title={openFile}>
        {loadingVersions && <Center p="lg"><Loader /></Center>}
        {versions && (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">{versions.versions.length} distinct versions, oldest first.</Text>
            {versions.versions.map((v) => (
              <Card key={v.id} padding="sm" radius="md" withBorder
                style={{ borderLeft: '3px solid var(--mantine-color-teal-6)' }}>
                <Stack gap={4}>
                  <Group justify="space-between">
                    <Badge color="teal">v{v.version_number}</Badge>
                    <Text size="xs" c="dimmed">{Math.round(v.size_bytes / 1024)} KB</Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    First seen {shortDate(v.created_at)}
                    {v.copies.length > 1 && ` · ${v.copies.length} copies`}
                  </Text>
                  <Text size="xs" ff="monospace" c="dimmed" lineClamp={1}>{v.content_hash.slice(0, 24)}…</Text>
                  <Stack gap={2}>
                    {v.copies.slice(0, 5).map((c) => (
                      <Paper key={c.id} p={4} style={{ cursor: 'pointer' }}
                        onClick={() => postToHost({ type: 'open_in_outlook', message_id: c.message_id })}>
                        <Text size="xs" lineClamp={1}>
                          {c.subject ?? '(no subject)'} — {c.from_addr ?? '?'}
                          {c.received_at && ` · ${shortDate(c.received_at)}`}
                        </Text>
                      </Paper>
                    ))}
                  </Stack>
                </Stack>
              </Card>
            ))}
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}

export function AutomationsSection(): React.JSX.Element {
  const [instruction, setInstruction] = useState('');
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState<Awaited<ReturnType<typeof api.addinRuleFromNL>> | null>(null);
  const [simulation, setSimulation] = useState<Awaited<ReturnType<typeof api.addinRuleSimulate>> | null>(null);
  const [simulating, setSimulating] = useState(false);
  const [scheduled, setScheduled] = useState<Awaited<ReturnType<typeof api.addinScheduledRules>> | null>(null);

  async function loadScheduled() {
    setScheduled(await api.addinScheduledRules());
  }

  useEffect(() => { loadScheduled(); }, []);

  async function generate() {
    if (instruction.trim().length < 5) return;
    setGenerating(true);
    setDraft(null);
    setSimulation(null);
    try {
      setDraft(await api.addinRuleFromNL(instruction.trim()));
    } catch (e) {
      notifications.show({ color: 'red', message: friendlyAiError(e) });
    } finally { setGenerating(false); }
  }

  async function simulate() {
    if (!draft) return;
    setSimulating(true);
    try {
      setSimulation(
        await api.addinRuleSimulate({
          when: draft.rule.when,
          then: draft.rule.then,
          days: 30,
        }),
      );
    } catch (e) {
      notifications.show({ color: 'red', message: friendlyAiError(e) });
    } finally { setSimulating(false); }
  }

  async function saveAsRule() {
    if (!draft) return;
    try {
      await api.addinRuleCreate({
        name: draft.rule.name || instruction.slice(0, 40),
        when: draft.rule.when,
        then: draft.rule.then,
      });
      notifications.show({ message: 'Rule saved.' });
      setDraft(null);
      setSimulation(null);
      setInstruction('');
    } catch (e) {
      notifications.show({ color: 'red', message: friendlyAiError(e) });
    }
  }

  async function runRuleNow(id: string) {
    try {
      const r = await api.addinRuleRunNow(id);
      notifications.show({
        message: `Matched ${r.matched_count} message${r.matched_count === 1 ? '' : 's'}.`,
      });
      await loadScheduled();
    } catch (e) {
      notifications.show({ color: 'red', message: friendlyAiError(e) });
    }
  }

  return (
    <Stack gap="md">
      <Card padding="md" radius="md" withBorder>
        <Stack gap="sm">
          <Group gap="xs">
            <ThemeIcon variant="light" color="grape">
              <IconWand {...ICON_PROPS} />
            </ThemeIcon>
            <Stack gap={0}>
              <Title order={5}>Automation in plain English</Title>
              <Text size="xs" c="dimmed">
                Type a rule like "every Friday delete server alerts older than 7 days". The model converts it to a rule schema you can preview before saving.
              </Text>
            </Stack>
          </Group>
          <Textarea
            placeholder='e.g. "every Monday at 8am, archive newsletters from last week"'
            autosize minRows={2} maxRows={4}
            value={instruction}
            onChange={(e) => setInstruction(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button size="xs" leftSection={<IconSparkles {...ICON_PROPS} />} loading={generating} onClick={generate} disabled={instruction.trim().length < 5}>
              Generate rule
            </Button>
          </Group>

          {draft && (
            <Card padding="sm" radius="md" withBorder
              style={{ borderLeft: '3px solid var(--mantine-color-grape-6)' }}>
              <Stack gap="xs">
                <Text fw={600} size="sm">{draft.rule.name}</Text>
                <Text size="xs" ff="monospace" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                  WHEN: {JSON.stringify(draft.rule.when, null, 2)}
                </Text>
                <Text size="xs" ff="monospace" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                  THEN: {JSON.stringify(draft.rule.then, null, 2)}
                </Text>
                {draft.rule.schedule && (
                  <Badge color="grape" variant="light">Schedule: {draft.rule.schedule}</Badge>
                )}
                <Group gap="xs">
                  <Button size="xs" variant="default" leftSection={<IconBolt {...ICON_PROPS} />} loading={simulating} onClick={simulate}>
                    Simulate (last 30d)
                  </Button>
                  <Button size="xs" leftSection={<IconCheck {...ICON_PROPS} />} onClick={saveAsRule}>
                    Save as rule
                  </Button>
                </Group>
              </Stack>
            </Card>
          )}

          {simulation && (
            <Card padding="sm" radius="md" withBorder
              style={{ borderLeft: '3px solid var(--mantine-color-blue-6)' }}>
              <Stack gap="xs">
                <Text fw={500} size="sm">Simulation</Text>
                <Text size="sm">{simulation.summary}</Text>
                {simulation.preview.length > 0 && (
                  <>
                    <Divider label="Preview (first 30)" labelPosition="left" />
                    <Stack gap={2}>
                      {simulation.preview.slice(0, 10).map((m) => (
                        <Paper key={m.message_id} p={4} style={{ cursor: 'pointer' }}
                          onClick={() => postToHost({ type: 'open_in_outlook', message_id: m.message_id })}>
                          <Group justify="space-between" gap="xs">
                            <Text size="xs" lineClamp={1} style={{ flex: 1 }}>
                              {m.subject ?? '(no subject)'} — {m.from_addr}
                            </Text>
                            {m.classification_tier && <Badge size="xs" color={tierColor(m.classification_tier)}>{m.classification_tier}</Badge>}
                          </Group>
                        </Paper>
                      ))}
                      {simulation.total_matches > 10 && (
                        <Text size="xs" c="dimmed" ta="center">+{simulation.total_matches - 10} more</Text>
                      )}
                    </Stack>
                  </>
                )}
              </Stack>
            </Card>
          )}
        </Stack>
      </Card>

      <Card padding="md" radius="md" withBorder>
        <Stack gap="sm">
          <Title order={6}>Scheduled rules</Title>
          <Text size="xs" c="dimmed">
            Rules with a schedule run automatically. The brain ticks every 5 minutes and fires anything due. Use "Run now" to fire on demand.
          </Text>
          {(scheduled?.rules ?? []).length === 0 && (
            <Alert color="gray" variant="light">No scheduled rules yet. Generate one above and save it.</Alert>
          )}
          {(scheduled?.rules ?? []).map((r) => (
            <Paper key={r.id} p="sm" withBorder>
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500} lineClamp={1}>{r.name}</Text>
                  <Text size="xs" c="dimmed">
                    {r.schedule}
                    {r.last_run_at && ` · last ran ${formatRelative(r.last_run_at)}`}
                    {r.run_count !== undefined && ` · ${r.run_count} runs`}
                  </Text>
                </Stack>
                <Button size="compact-xs" variant="default" onClick={() => runRuleNow(r.id)}>
                  Run now
                </Button>
              </Group>
            </Paper>
          ))}
        </Stack>
      </Card>
    </Stack>
  );
}

export function DecisionsSection(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinDecisionsList>> | null>(null);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [participantFilter, setParticipantFilter] = useState<string | null>(() => {
    try { return localStorage.getItem('addin_decisions_participant'); } catch { return null; }
  });
  const [decisionsSortBy, setDecisionsSortBy] = useState<'date_desc' | 'date_asc' | 'participants'>(() => {
    try { return (localStorage.getItem('addin_decisions_sort') as 'date_desc' | 'date_asc' | 'participants') ?? 'date_desc'; }
    catch { return 'date_desc'; }
  });
  const [participantsScope, setParticipantsScope] = useState<'any' | 'with' | 'solo'>(() => {
    try { return (localStorage.getItem('addin_decisions_scope') as 'any' | 'with' | 'solo') ?? 'any'; }
    catch { return 'any'; }
  });
  useEffect(() => {
    try {
      if (participantFilter == null) localStorage.removeItem('addin_decisions_participant');
      else localStorage.setItem('addin_decisions_participant', participantFilter);
    } catch { /* ignore */ }
  }, [participantFilter]);
  useEffect(() => { try { localStorage.setItem('addin_decisions_sort', decisionsSortBy); } catch { /* ignore */ } }, [decisionsSortBy]);
  useEffect(() => { try { localStorage.setItem('addin_decisions_scope', participantsScope); } catch { /* ignore */ } }, [participantsScope]);
  const [linkedPromises, setLinkedPromises] = useState<Awaited<ReturnType<typeof api.addinPromisesList>>['promises']>([]);
  useEffect(() => {
    api.addinPromisesList('pending').then((r) => setLinkedPromises(r.promises)).catch(() => { /* silent */ });
  }, []);
  const [decisionCaseMap, setDecisionCaseMap] = useState<Map<string, { case_id: string; case_name: string; status: string }>>(new Map());

  useEffect(() => {
    api.addinCasesMembership().then((r) => {
      const m = new Map<string, { case_id: string; case_name: string; status: string }>();
      for (const x of r.memberships) m.set(x.message_id, { case_id: x.case_id, case_name: x.case_name, status: x.status });
      setDecisionCaseMap(m);
    }).catch(() => { /* silent */ });
  }, []);

  // Top participants computed from the loaded decisions.
  const decisionRows = data?.decisions ?? [];
  function parseParticipants(json: string): string[] {
    try { return JSON.parse(json) as string[]; } catch { return []; }
  }
  const participantCounts = new Map<string, number>();
  for (const d of decisionRows) {
    for (const p of parseParticipants(d.participants_json)) {
      const k = p.trim().toLowerCase();
      if (!k) continue;
      participantCounts.set(k, (participantCounts.get(k) ?? 0) + 1);
    }
  }
  const topParticipants = [...participantCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, count]) => ({ name, count }));
  const filteredDecisions = (participantFilter
    ? decisionRows.filter((d) =>
        parseParticipants(d.participants_json).some((p) => p.trim().toLowerCase() === participantFilter),
      )
    : decisionRows
  ).filter((d) => {
    if (participantsScope === 'any') return true;
    const has = parseParticipants(d.participants_json).length > 0;
    return participantsScope === 'with' ? has : !has;
  }).slice().sort((a, b) => {
    if (decisionsSortBy === 'date_asc') return a.decided_at.localeCompare(b.decided_at);
    if (decisionsSortBy === 'participants') {
      return parseParticipants(b.participants_json).length - parseParticipants(a.participants_json).length;
    }
    return b.decided_at.localeCompare(a.decided_at);
  });

  async function load() {
    setBusy(true);
    try { setData(await api.addinDecisionsList()); } finally { setBusy(false); }
  }
  async function scan() {
    setScanning(true);
    try {
      const r = await api.addinDecisionsScan(7);
      notifications.show({ message: `Scanned ${r.scanned} threads, extracted ${r.extracted} decision${r.extracted === 1 ? '' : 's'}.` });
      await load();
    } catch (e) {
      notifications.show({ color: 'red', message: friendlyAiError(e) });
    } finally { setScanning(false); }
  }
  useEffect(() => { load(); }, []);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Concrete decisions extracted from your recent threads — every "we agreed to X" you'd otherwise lose.
        </Text>
        <Group gap={4}>
          {filteredDecisions.length > 0 && (
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconFileText size={12} />}
              onClick={() => {
                const esc = (s: string | null | undefined) => {
                  if (s == null) return '';
                  const v = String(s);
                  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
                };
                const header = ['decision_text', 'decided_at', 'subject', 'participants', 'message_id'].join(',');
                // Filter-aware: export currently-visible rows.
                const lines = [
                  header,
                  ...filteredDecisions.map((d) => {
                    const parts = parseParticipants(d.participants_json);
                    return [d.decision_text, d.decided_at, d.subject, parts.join('; '), d.message_id].map(esc).join(',');
                  }),
                ];
                const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const tag = participantFilter ? `-with-${participantFilter.replace(/[^a-z0-9]/gi, '_').slice(0, 24)}` : '';
                a.download = `decisions${tag}-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              }}
            >
              CSV ({filteredDecisions.length})
            </Button>
          )}
          <Button size="xs" leftSection={<IconSparkles {...ICON_PROPS} />} loading={scanning} onClick={scan}>
            Scan last 7 days
          </Button>
        </Group>
      </Group>
      {busy && !data ? <Center p="lg"><Loader /></Center> : null}
      {data && data.decisions.length === 0 && (
        <Alert color="gray" variant="light">No tracked decisions. Click "Scan" to extract from recent threads.</Alert>
      )}
      {(decisionRows.length > 0) && (
        <Group justify="space-between" wrap="wrap">
          {/* Weekly sparkline — last 8 weeks */}
          {(() => {
            const buckets: number[] = new Array(8).fill(0);
            const now = Date.now();
            for (const d of decisionRows) {
              const t = new Date(d.decided_at).getTime();
              const weeksAgo = Math.floor((now - t) / (7 * 86400000));
              if (weeksAgo >= 0 && weeksAgo < 8) buckets[7 - weeksAgo] = (buckets[7 - weeksAgo] ?? 0) + 1;
            }
            const max = Math.max(1, ...buckets);
            return (
              <Tooltip label={`Decisions per week (last 8w): ${buckets.join(', ')}`}>
                <Group gap={2} align="flex-end" h={28} style={{ width: 96 }}>
                  {buckets.map((n, i) => (
                    <div key={i} style={{
                      flex: 1,
                      height: `${Math.max(2, (n / max) * 100)}%`,
                      background: 'var(--mantine-color-teal-5)',
                      borderRadius: 1,
                    }} />
                  ))}
                </Group>
              </Tooltip>
            );
          })()}
          <Select
            size="xs"
            w={170}
            value={decisionsSortBy}
            onChange={(v) => v && setDecisionsSortBy(v as 'date_desc' | 'date_asc' | 'participants')}
            data={[
              { value: 'date_desc', label: 'Newest first' },
              { value: 'date_asc', label: 'Oldest first' },
              { value: 'participants', label: 'Most participants' },
            ]}
          />
        </Group>
      )}
      {decisionRows.length > 0 && (
        <Group gap={4} wrap="wrap">
          <Text size="xs" c="dimmed">Scope:</Text>
          {([
            { k: 'any', label: 'Any' },
            { k: 'with', label: 'With participants' },
            { k: 'solo', label: 'Solo' },
          ] as const).map((o) => (
            <Badge key={o.k} size="xs" variant={participantsScope === o.k ? 'filled' : 'light'}
              color="teal" style={{ cursor: 'pointer' }}
              onClick={() => setParticipantsScope(o.k)}>
              {o.label}
            </Badge>
          ))}
        </Group>
      )}
      {topParticipants.length > 0 && (
        <Group gap={4} wrap="wrap">
          <Text size="xs" c="dimmed">Participant:</Text>
          <Badge size="xs" variant={participantFilter === null ? 'filled' : 'light'} color="teal"
            style={{ cursor: 'pointer' }} onClick={() => setParticipantFilter(null)}>
            All {decisionRows.length}
          </Badge>
          {topParticipants.map((p) => (
            <Badge
              key={p.name}
              size="xs"
              variant={participantFilter === p.name ? 'filled' : 'light'}
              color="teal"
              style={{ cursor: 'pointer', maxWidth: 200 }}
              onClick={() => setParticipantFilter(participantFilter === p.name ? null : p.name)}
            >
              <Text size="xs" lineClamp={1}>{p.name} {p.count}</Text>
            </Badge>
          ))}
        </Group>
      )}
      {participantFilter && filteredDecisions.length === 0 && (
        <Text size="xs" c="dimmed" ta="center" py="md">
          No decisions involving {participantFilter}. Pick a different chip.
        </Text>
      )}
      <Stack gap="xs">
        {filteredDecisions.map((d) => {
          let participants: string[] = [];
          try { participants = JSON.parse(d.participants_json) as string[]; } catch { /* ignore */ }
          const caseInfo = decisionCaseMap.get(d.message_id);
          return (
            <Card key={d.id} padding="sm" radius="md" withBorder>
              <Stack gap={4}>
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" fw={500} style={{ flex: 1, minWidth: 0 }}>{d.decision_text}</Text>
                  <Tooltip label="Edit text">
                    <ActionIcon size="sm" variant="subtle" onClick={async () => {
                      const next = prompt('Edit decision text:', d.decision_text);
                      if (next === null || next.trim() === d.decision_text.trim()) return;
                      try {
                        await api.addinDecisionPatch(d.id, { decision_text: next.trim() });
                        notifications.show({ message: 'Decision updated.' });
                        load();
                      } catch (e) {
                        notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
                      }
                    }}>
                      <IconPencil {...ICON_PROPS} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Copy as text">
                    <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => {
                      const lines = [
                        d.decision_text,
                        `Decided: ${d.decided_at.slice(0, 10)}`,
                        d.subject ? `In: ${d.subject}` : '',
                        participants.length > 0 ? `Participants: ${participants.join(', ')}` : '',
                      ].filter(Boolean).join('\n');
                      navigator.clipboard.writeText(lines)
                        .then(() => notifications.show({ message: 'Decision copied.' }))
                        .catch(() => notifications.show({ color: 'red', message: 'Copy failed' }));
                    }}>
                      <IconFileText {...ICON_PROPS} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Delete">
                    <ActionIcon size="sm" variant="subtle" color="red" onClick={async () => {
                      if (!confirm('Delete this decision?')) return;
                      try {
                        await api.addinDecisionDelete(d.id);
                        notifications.show({ color: 'orange', message: 'Decision deleted.' });
                        load();
                      } catch (e) {
                        notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
                      }
                    }}>
                      <IconTrash {...ICON_PROPS} />
                    </ActionIcon>
                  </Tooltip>
                  {caseInfo && (
                    <Tooltip label={`Linked case: ${caseInfo.case_name} (${caseInfo.status})`}>
                      <Badge size="xs" color={caseInfo.status === 'open' ? 'blue' : 'gray'}
                        variant="light" leftSection={<IconFolder size={9} />}
                        style={{ cursor: 'pointer', maxWidth: 140 }}
                        onClick={() => {
                          sessionStorage.setItem('addin_open_case', caseInfo.case_id);
                          window.dispatchEvent(new Event('ai-outlook:open-case'));
                        }}
                      >
                        <Text size="xs" lineClamp={1}>{caseInfo.case_name}</Text>
                      </Badge>
                    </Tooltip>
                  )}
                </Group>
                <Text size="xs" c="dimmed">
                  {shortDate(d.decided_at)}
                  {d.subject && ` · in ${d.subject}`}
                  {participants.length > 0 && ` · ${participants.join(', ')}`}
                </Text>
                {(() => {
                  const decisionTime = new Date(d.decided_at).getTime();
                  const window = 7 * 86400000;
                  const linked = linkedPromises.filter((p) =>
                    p.due_at && Math.abs(new Date(p.due_at).getTime() - decisionTime) <= window,
                  );
                  if (linked.length === 0) return null;
                  return (
                    <Group gap={4} wrap="wrap" mt={4}>
                      <Text size="xs" c="dimmed">Promises near this date:</Text>
                      {linked.slice(0, 3).map((p) => (
                        <Tooltip key={p.id} label={p.promise_text}>
                          <Badge size="xs" variant="light" color="orange" style={{ maxWidth: 220 }}>
                            <Text size="xs" lineClamp={1}>
                              {p.promise_text.slice(0, 40)} · due {p.due_at?.slice(0, 10)}
                            </Text>
                          </Badge>
                        </Tooltip>
                      ))}
                      {linked.length > 3 && (
                        <Badge size="xs" variant="dot">+{linked.length - 3}</Badge>
                      )}
                    </Group>
                  );
                })()}
                <Group gap={4}>
                  <Button size="compact-xs" variant="subtle" onClick={() => postToHost({ type: 'open_in_outlook', message_id: d.message_id })}>
                    Open thread
                  </Button>
                </Group>
              </Stack>
            </Card>
          );
        })}
      </Stack>
    </Stack>
  );
}

export function ContradictionsSection(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinContradictionsList>> | null>(null);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    setBusy(true);
    try { setData(await api.addinContradictionsList()); } finally { setBusy(false); }
  }
  async function scan() {
    setScanning(true);
    try {
      const r = await api.addinContradictionsScan(7);
      notifications.show({ message: `Scanned. Flagged ${r.flagged} contradiction${r.flagged === 1 ? '' : 's'}.` });
      await load();
    } catch (e) {
      notifications.show({ color: 'red', message: friendlyAiError(e) });
    } finally { setScanning(false); }
  }
  async function dismiss(id: string) {
    await api.addinContradictionDismiss(id);
    await load();
  }
  useEffect(() => { load(); }, []);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          LLM checks your sent mail for self-contradictions — different deadlines / numbers / commitments to different people.
        </Text>
        <Button size="xs" leftSection={<IconSparkles {...ICON_PROPS} />} loading={scanning} onClick={scan}>
          Scan
        </Button>
      </Group>
      {busy && !data ? <Center p="lg"><Loader /></Center> : null}
      {data && data.items.length === 0 && (
        <Alert color="green" variant="light">No contradictions detected.</Alert>
      )}
      {(data?.items ?? []).map((c) => (
        <Card key={c.id} padding="md" radius="md" withBorder
          style={{ borderLeft: `4px solid var(--mantine-color-${c.severity === 'high' ? 'red' : c.severity === 'medium' ? 'orange' : 'yellow'}-6)` }}>
          <Stack gap="xs">
            <Group justify="space-between">
              <Badge color={c.severity === 'high' ? 'red' : c.severity === 'medium' ? 'orange' : 'yellow'}>
                {c.severity} severity
              </Badge>
              <Button size="compact-xs" variant="subtle" onClick={() => dismiss(c.id)}>
                Dismiss
              </Button>
            </Group>
            <Text size="sm">{c.explanation}</Text>
            <Paper p="xs" withBorder>
              <Text size="xs" c="dimmed" mb={2}>Email A: {c.subject_a ?? '(no subject)'}</Text>
              <Text size="sm">"{c.claim_a_text}"</Text>
              <Button size="compact-xs" variant="subtle" mt={4} onClick={() => postToHost({ type: 'open_in_outlook', message_id: c.claim_a_message_id })}>
                Open A
              </Button>
            </Paper>
            <Paper p="xs" withBorder>
              <Text size="xs" c="dimmed" mb={2}>Email B: {c.subject_b ?? '(no subject)'}</Text>
              <Text size="sm">"{c.claim_b_text}"</Text>
              <Button size="compact-xs" variant="subtle" mt={4} onClick={() => postToHost({ type: 'open_in_outlook', message_id: c.claim_b_message_id })}>
                Open B
              </Button>
            </Paper>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}

export function ImportantNotUrgentSection(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinImportantNotUrgent>> | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setBusy(true);
    api.addinImportantNotUrgent().then(setData).finally(() => setBusy(false));
  }, []);
  if (busy && !data) return <Center p="lg"><Loader /></Center>;
  if (!data || data.items.length === 0) return <Alert color="gray" variant="light">Nothing in this queue.</Alert>;
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">P1/P2 emails older than 24h, not snoozed. The "intentional, not reactive" queue.</Text>
      {data.items.map((m) => (
        <Paper key={m.id} p="xs" style={{ cursor: 'pointer' }}
          onClick={() => postToHost({ type: 'open_in_outlook', message_id: m.message_id })}>
          <Group justify="space-between" wrap="nowrap">
            <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={500} lineClamp={1}>{m.subject ?? '(no subject)'}</Text>
              <Text size="xs" c="dimmed" lineClamp={1}>{m.from_addr} · {shortDate(m.received_at)}</Text>
            </Stack>
            {m.classification_tier && <Badge color={tierColor(m.classification_tier)}>{m.classification_tier}</Badge>}
          </Group>
        </Paper>
      ))}
    </Stack>
  );
}

export function SlaSection(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinSlaViolations>> | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setBusy(true);
    api.addinSlaViolations().then(setData).finally(() => setBusy(false));
  }, []);
  if (busy && !data) return <Center p="lg"><Loader /></Center>;
  if (!data || data.violations.length === 0) return <Alert color="green" variant="light">No SLA breaches.</Alert>;
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">
        VIP P0 → 1h, VIP → 4h, P0 → 2h, P1 → 8h. Past those windows, here's what's overdue.
      </Text>
      {data.violations.map((v) => (
        <Paper key={v.id} p="xs" style={{ cursor: 'pointer', borderLeft: '3px solid var(--mantine-color-red-6)' }}
          onClick={() => postToHost({ type: 'open_in_outlook', message_id: v.message_id })}>
          <Group justify="space-between" wrap="nowrap">
            <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
              <Group gap={4}>
                {v.is_vip && <IconStarFilled size={14} color="var(--mantine-color-yellow-6)" />}
                <Text size="sm" fw={500} lineClamp={1}>{v.subject ?? '(no subject)'}</Text>
              </Group>
              <Text size="xs" c="dimmed">
                {v.from_addr} · {v.age_hours.toFixed(1)}h old · SLA {v.sla_hours}h · {v.breach_hours.toFixed(1)}h overdue
              </Text>
            </Stack>
            <Badge color="red">{v.classification_tier}</Badge>
          </Group>
        </Paper>
      ))}
    </Stack>
  );
}

export function ResolvedSection(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinResolvedThreads>> | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setBusy(true);
    api.addinResolvedThreads().then(setData).finally(() => setBusy(false));
  }, []);
  if (busy && !data) return <Center p="lg"><Loader /></Center>;
  if (!data || data.items.length === 0) return <Alert color="gray" variant="light">Nothing detected as resolved.</Alert>;
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">
        Threads where the last inbound message looks like closure ("thanks", "resolved", "got it") and you've been silent ≥3 days. Safe to archive.
      </Text>
      {data.items.map((m) => (
        <Paper key={m.id} p="xs" style={{ cursor: 'pointer' }}
          onClick={() => postToHost({ type: 'open_in_outlook', message_id: m.message_id })}>
          <Stack gap={2}>
            <Text size="sm" fw={500} lineClamp={1}>{m.subject ?? '(no subject)'}</Text>
            <Text size="xs" c="dimmed" lineClamp={1}>{m.from_addr} · {shortDate(m.received_at)}</Text>
            <Text size="xs" c="dimmed" lineClamp={2} style={{ fontStyle: 'italic' }}>"{m.snippet}"</Text>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

export function UnsubscribeSection(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinUnsubscribeSuggestions>> | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setBusy(true);
    api.addinUnsubscribeSuggestions().then(setData).finally(() => setBusy(false));
  }, []);
  if (busy && !data) return <Center p="lg"><Loader /></Center>;
  if (!data || data.items.length === 0) return <Alert color="gray" variant="light">No unsubscribe candidates.</Alert>;
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">
        Newsletters / notifications senders sending you 5+ in last 30 days. Click to open one and use the unsubscribe link.
      </Text>
      {data.items.map((m) => (
        <Paper key={m.from_addr} p="xs">
          <Group justify="space-between" wrap="nowrap">
            <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={500} lineClamp={1}>{m.from_addr}</Text>
              <Text size="xs" c="dimmed">{m.volume_30d} messages · last 30 days</Text>
            </Stack>
            <Group gap={4}>
              <Button size="compact-xs" variant="subtle" onClick={() => postToHost({ type: 'open_in_outlook', message_id: m.sample_message_id })}>
                Open sample
              </Button>
            </Group>
          </Group>
        </Paper>
      ))}
    </Stack>
  );
}

// =========================================================================
// PHASE 1 — NOISE KILLER (Round 48)
// 4-step Stepper: Diagnose → Pick clusters → Preview/Exceptions → Activate.
// Reuses existing rules table; activation tagged with undo_token for 24h
// rollback. Default mode = Tag (label only, never destructive) unless user
// explicitly picks Archive — and even then we surface risky-term warnings.
// =========================================================================

export function NoiseKillerSection(): React.JSX.Element {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [diag, setDiag] = useState<Awaited<ReturnType<typeof api.addinNoiseKillerDiagnose>> | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [action, setAction] = useState<'archive' | 'tag' | 'digest'>('tag');
  const [exceptionTerms, setExceptionTerms] = useState('invoice\npayment\nbank\nfailed\nurgent\nsecurity\npassword\ncontract');
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof api.addinNoiseKillerPreview>> | null>(null);
  const [activated, setActivated] = useState<Awaited<ReturnType<typeof api.addinNoiseKillerActivate>> | null>(null);

  async function diagnose() {
    setBusy(true);
    try {
      const r = await api.addinNoiseKillerDiagnose(30);
      setDiag(r);
      // Pre-pick top 5 risk-free clusters as a sensible default
      const safe = r.clusters
        .filter((c) => c.risky_terms_in_sample.length === 0 && c.replied_to_count === 0)
        .slice(0, 5);
      setPicked(new Set(safe.map((c) => c.cluster_id)));
      setStep(1);
    } catch (e) {
      notifications.show({ color: 'red', message: friendlyAiError(e) });
    } finally { setBusy(false); }
  }

  function pickedSenders(): string[] {
    if (!diag) return [];
    return diag.clusters
      .filter((c) => picked.has(c.cluster_id) && c.sender_pattern)
      .map((c) => c.sender_pattern as string);
  }

  async function runPreview() {
    setBusy(true);
    try {
      const senders = pickedSenders();
      if (senders.length === 0) {
        notifications.show({ color: 'orange', message: 'Pick at least one cluster first.' });
        setBusy(false);
        return;
      }
      const exceptions = {
        skip_if_subject_contains: exceptionTerms.split('\n').map((s) => s.trim()).filter((s) => s.length > 0),
      };
      const r = await api.addinNoiseKillerPreview({
        cluster_ids: [...picked],
        sender_patterns: senders,
        action,
        exceptions,
      });
      setPreview(r);
      setStep(2);
    } catch (e) {
      notifications.show({ color: 'red', message: friendlyAiError(e) });
    } finally { setBusy(false); }
  }

  async function activate() {
    setBusy(true);
    try {
      const senders = pickedSenders();
      const exceptions = {
        skip_if_subject_contains: exceptionTerms.split('\n').map((s) => s.trim()).filter((s) => s.length > 0),
      };
      const r = await api.addinNoiseKillerActivate({
        sender_patterns: senders,
        action,
        exceptions,
        label: 'machine',
      });
      setActivated(r);
      notifications.show({ color: 'green', message: `Activated ${r.rules_created} rules. 24h undo available.` });
      setStep(3);
    } catch (e) {
      notifications.show({ color: 'red', message: friendlyAiError(e) });
    } finally { setBusy(false); }
  }

  async function undo() {
    if (!activated) return;
    setBusy(true);
    try {
      const r = await api.addinNoiseKillerUndo(activated.undo_token);
      notifications.show({ color: 'orange', message: `Undone — ${r.undone} rules disabled.` });
      setActivated(null);
      setStep(0);
      setDiag(null);
      setPicked(new Set());
      setPreview(null);
    } catch (e) {
      notifications.show({ color: 'red', message: friendlyAiError(e) });
    } finally { setBusy(false); }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={0}>
          <Title order={4}>Noise Killer</Title>
          <Text size="xs" c="dimmed">
            Find machine-email patterns that account for most of your inbox volume,
            preview what would be hidden, then activate with a 24h undo window.
          </Text>
        </Stack>
        {step > 0 && (
          <Button size="xs" variant="subtle" onClick={() => { setStep(0); setDiag(null); setPicked(new Set()); setPreview(null); setActivated(null); }}>
            Reset
          </Button>
        )}
      </Group>

      {/* STEP 0 — Intro / Diagnose trigger */}
      {step === 0 && (
        <Card padding="lg" radius="md" withBorder
          style={{ borderLeft: '4px solid var(--mantine-color-violet-6)' }}>
          <Stack gap="sm">
            <Group gap="xs">
              <ThemeIcon variant="light" color="violet" size="lg">
                <IconBolt {...ICON_PROPS} />
              </ThemeIcon>
              <Text fw={600} size="lg">Find your inbox noise</Text>
            </Group>
            <Text size="sm" c="dimmed">
              We'll scan the last 30 days for high-volume machine senders, flapping server alerts,
              and exact-duplicate emails. You'll get a list of clusters with recommended actions —
              nothing happens until you explicitly activate.
            </Text>
            <Button onClick={diagnose} loading={busy} size="md" leftSection={<IconSparkles {...ICON_PROPS} />}>
              Diagnose noise patterns
            </Button>
          </Stack>
        </Card>
      )}

      {/* STEP 1 — Pick clusters */}
      {step === 1 && diag && (
        <>
          <Card padding="md" radius="md" withBorder
            style={{ borderLeft: '4px solid var(--mantine-color-blue-6)' }}>
            <Group gap="xl">
              <Stack gap={0} align="center">
                <Text fw={700} size="xl">{diag.total_messages_in_window.toLocaleString()}</Text>
                <Text size="xs" c="dimmed">Total {diag.window_days}d</Text>
              </Stack>
              <Stack gap={0} align="center">
                <Text fw={700} size="xl" c="orange">{diag.noise_messages_estimated.toLocaleString()}</Text>
                <Text size="xs" c="dimmed">Noise (~{diag.noise_pct_estimated}%)</Text>
              </Stack>
              <Stack gap={0} align="center">
                <Text fw={700} size="xl" c="violet">{diag.clusters.length}</Text>
                <Text size="xs" c="dimmed">Patterns found</Text>
              </Stack>
              <Stack gap={0} align="center">
                <Text fw={700} size="xl" c="green">{picked.size}</Text>
                <Text size="xs" c="dimmed">Selected</Text>
              </Stack>
            </Group>
          </Card>

          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              Select patterns to clean. Items with risky terms or past replies are NOT pre-selected — you can opt them in but review carefully.
            </Text>
            <Group gap="xs">
              <Button size="compact-xs" variant="default" onClick={() => {
                const all = diag.clusters.filter((c) => c.risky_terms_in_sample.length === 0 && c.replied_to_count === 0);
                setPicked(new Set(all.map((c) => c.cluster_id)));
              }}>Select safe</Button>
              <Button size="compact-xs" variant="subtle" onClick={() => setPicked(new Set())}>Clear</Button>
            </Group>
          </Group>

          <Stack gap="xs">
            {diag.clusters.map((c) => {
              const isPicked = picked.has(c.cluster_id);
              const isRisky = c.risky_terms_in_sample.length > 0 || c.replied_to_count > 0;
              return (
                <Paper key={c.cluster_id} p="sm" withBorder
                  style={{
                    cursor: 'pointer',
                    borderLeft: '3px solid var(--mantine-color-' + (isPicked ? 'green' : isRisky ? 'orange' : 'gray') + '-5)',
                    background: isPicked ? 'var(--mantine-color-green-0)' : undefined,
                  }}
                  onClick={() => {
                    const next = new Set(picked);
                    if (next.has(c.cluster_id)) next.delete(c.cluster_id);
                    else next.add(c.cluster_id);
                    setPicked(next);
                  }}>
                  <Group justify="space-between" wrap="nowrap" gap="xs" align="flex-start">
                    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                      <Group gap="xs" wrap="nowrap">
                        <Checkbox checked={isPicked} onChange={() => {}} />
                        <Text size="sm" fw={500} lineClamp={1}>{c.title}</Text>
                        <Badge size="xs" color={c.kind === 'duplicate' ? 'red' : c.kind === 'recovery_pair' ? 'blue' : 'gray'}>
                          {c.kind === 'sender_volume' ? 'volume' : c.kind === 'recovery_pair' ? 'flapping' : c.kind}
                        </Badge>
                      </Group>
                      <Text size="xs" c="dimmed" lineClamp={2}>{c.reason}</Text>
                      {c.risky_terms_in_sample.length > 0 && (
                        <Group gap={2}>
                          {c.risky_terms_in_sample.map((r) => (
                            <Badge key={r} size="xs" color="orange" variant="light">⚠ {r}</Badge>
                          ))}
                        </Group>
                      )}
                      {c.replied_to_count > 0 && (
                        <Badge size="xs" color="orange" variant="light">⚠ You replied to this sender {c.replied_to_count}× — verify before archiving</Badge>
                      )}
                    </Stack>
                    <Stack gap={0} align="flex-end" style={{ minWidth: 110 }}>
                      <Text fw={700} size="md">{c.count_in_window.toLocaleString()}</Text>
                      <Text size="xs" c="dimmed">~{c.est_time_saved_min}m saved</Text>
                    </Stack>
                  </Group>
                </Paper>
              );
            })}
          </Stack>

          <Group justify="space-between" mt="md">
            <Button variant="subtle" onClick={() => setStep(0)}>← Re-diagnose</Button>
            <Button onClick={runPreview} loading={busy} disabled={picked.size === 0}>
              Continue: configure & preview →
            </Button>
          </Group>
        </>
      )}

      {/* STEP 2 — Configure action + exceptions, preview */}
      {step === 2 && diag && preview && (
        <>
          <Card padding="md" radius="md" withBorder>
            <Stack gap="sm">
              <Title order={5}>Action</Title>
              <SegmentedControl
                value={action}
                onChange={(v) => setAction(v as 'archive' | 'tag' | 'digest')}
                data={[
                  { value: 'tag', label: 'Tag only (safest)' },
                  { value: 'digest', label: 'Daily digest' },
                  { value: 'archive', label: 'Archive + mark read' },
                ]}
              />
              <Text size="xs" c="dimmed">
                {action === 'tag' && 'Adds a "machine" label so you can filter — emails stay visible. No risk.'}
                {action === 'digest' && 'Hides individual emails from inbox; bundles them into a daily summary you review.'}
                {action === 'archive' && '⚠ Removes from inbox + marks read. You can still find via search. 24h undo available.'}
              </Text>
            </Stack>
          </Card>

          <Card padding="md" radius="md" withBorder>
            <Stack gap="sm">
              <Title order={5}>Safety exceptions</Title>
              <Text size="xs" c="dimmed">
                Skip emails whose subject contains ANY of these (one per line). Default list covers finance/legal/operational risk terms.
              </Text>
              <Textarea
                value={exceptionTerms}
                onChange={(e) => setExceptionTerms(e.currentTarget.value)}
                autosize minRows={4} maxRows={10}
                styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
              />
              <Group justify="flex-end">
                <Button size="compact-xs" variant="default" onClick={runPreview} loading={busy}>
                  Re-preview with these exceptions
                </Button>
              </Group>
            </Stack>
          </Card>

          <Card padding="md" radius="md" withBorder
            style={{ borderLeft: '4px solid var(--mantine-color-violet-6)' }}>
            <Stack gap="sm">
              <Title order={5}>Preview</Title>
              <Group gap="xl">
                <Stack gap={0} align="center">
                  <Text fw={700} size="xl" c="violet">{preview.would_affect.toLocaleString()}</Text>
                  <Text size="xs" c="dimmed">Would be {preview.action}d</Text>
                </Stack>
                <Stack gap={0} align="center">
                  <Text fw={700} size="xl" c="green">{preview.would_skip_due_to_exceptions.toLocaleString()}</Text>
                  <Text size="xs" c="dimmed">Skipped by safety</Text>
                </Stack>
              </Group>
              {preview.sample_affected.length > 0 && (
                <>
                  <Divider label={`First ${preview.sample_affected.length} that would be affected`} labelPosition="left" />
                  <Stack gap={2}>
                    {preview.sample_affected.slice(0, 10).map((m) => (
                      <Paper key={m.message_id} p={4} style={{ cursor: 'pointer' }}
                        onClick={() => postToHost({ type: 'open_in_outlook', message_id: m.message_id })}>
                        <Group justify="space-between" wrap="nowrap" gap="xs">
                          <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                            <Text size="xs" fw={500} lineClamp={1}>{m.subject ?? '(no subject)'}</Text>
                            <Text size="xs" c="dimmed" lineClamp={1}>{m.from_addr} · {shortDate(m.received_at)}</Text>
                          </Stack>
                          {m.classification_tier && <Badge size="xs" color={tierColor(m.classification_tier)}>{m.classification_tier}</Badge>}
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </>
              )}
            </Stack>
          </Card>

          <Group justify="space-between">
            <Button variant="subtle" onClick={() => setStep(1)}>← Back to clusters</Button>
            <Button color="violet" onClick={activate} loading={busy} leftSection={<IconCheck {...ICON_PROPS} />}>
              Activate {pickedSenders().length} rules (24h undo)
            </Button>
          </Group>
        </>
      )}

      {/* STEP 3 — Activated, undo available */}
      {step === 3 && activated && (
        <Card padding="lg" radius="md" withBorder
          style={{ borderLeft: '4px solid var(--mantine-color-green-6)' }}>
          <Stack gap="md" align="center">
            <ThemeIcon variant="light" color="green" size="xl">
              <IconCheck {...ICON_PROPS} />
            </ThemeIcon>
            <Text fw={700} size="lg">{activated.rules_created} rules activated</Text>
            <Text size="xs" c="dimmed" ta="center">
              Undo available until {new Date(activated.undo_until).toLocaleString()}.
              After that, undo individual rules from the Automation tab.
            </Text>
            <Group gap="xs">
              <Button color="orange" variant="light" onClick={undo} loading={busy}>
                Undo all {activated.rules_created} rules
              </Button>
              <Button variant="default" onClick={() => { setStep(0); setDiag(null); setPicked(new Set()); setPreview(null); setActivated(null); }}>
                Find more noise
              </Button>
            </Group>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

export function NoiseBudgetSection(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinNoiseBudget>> | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setBusy(true);
    api.addinNoiseBudget().then(setData).finally(() => setBusy(false));
  }, []);
  if (busy && !data) return <Center p="lg"><Loader /></Center>;
  if (!data || data.items.length === 0) return <Alert color="gray" variant="light">No high-volume senders.</Alert>;
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">
        Senders sending you 30+ in 30 days. Suggested budget = half their average. Use the rule builder to enforce.
      </Text>
      {data.items.map((s) => (
        <Paper key={s.from_addr} p="xs">
          <Group justify="space-between" wrap="nowrap">
            <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
              <Group gap={4}>
                <Text size="sm" fw={500} lineClamp={1}>{s.from_addr}</Text>
                {s.is_machine && <Badge size="xs" variant="light">machine</Badge>}
              </Group>
              <Text size="xs" c="dimmed">
                {s.total_30d} total · {s.daily_avg}/day avg · suggested cap {s.suggested_threshold}/day
              </Text>
            </Stack>
          </Group>
        </Paper>
      ))}
    </Stack>
  );
}

export function AntiVipSection(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinAntiVipsList>> | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    setBusy(true);
    try { setData(await api.addinAntiVipsList()); } finally { setBusy(false); }
  }
  async function add() {
    if (!newEmail.trim()) return;
    await api.addinAntiVipAdd(newEmail.trim(), reason.trim() || undefined);
    setNewEmail('');
    setReason('');
    await load();
  }
  async function remove(email: string) {
    await api.addinAntiVipRemove(email);
    await load();
  }
  useEffect(() => { load(); }, []);

  return (
    <Stack gap="md">
      <Text size="xs" c="dimmed">
        Anti-VIPs are senders you want auto-archived on sight. Mirror of the VIP list.
      </Text>
      <Card padding="sm" radius="md" withBorder>
        <Group gap="xs" wrap="nowrap" align="flex-end">
          <TextInput size="xs" label="Email" value={newEmail} onChange={(e) => setNewEmail(e.currentTarget.value)} style={{ flex: 1 }} />
          <TextInput size="xs" label="Reason (optional)" value={reason} onChange={(e) => setReason(e.currentTarget.value)} style={{ flex: 1 }} />
          <Button size="xs" onClick={add} leftSection={<IconPlus {...ICON_PROPS} />}>Add</Button>
        </Group>
      </Card>
      {busy && !data ? <Center p="lg"><Loader /></Center> : null}
      {(data?.items ?? []).length === 0 && !busy && (
        <Alert color="gray" variant="light">No anti-VIPs yet.</Alert>
      )}
      <Stack gap="xs">
        {(data?.items ?? []).map((v) => (
          <Paper key={v.email} p="xs" withBorder>
            <Group justify="space-between">
              <Stack gap={0}>
                <Text size="sm" fw={500}>{v.email}</Text>
                {v.reason && <Text size="xs" c="dimmed">{v.reason}</Text>}
              </Stack>
              <ActionIcon variant="subtle" color="red" onClick={() => remove(v.email)}>
                <IconTrash {...ICON_PROPS} />
              </ActionIcon>
            </Group>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
}

export function StatusReportSection(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinStatusReport>> | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setBusy(true);
    api.addinStatusReport().then(setData).finally(() => setBusy(false));
  }, []);
  if (busy && !data) return <Center p="lg"><Loader /></Center>;
  if (!data) return <Alert color="gray" variant="light">No data yet.</Alert>;
  const totalText = `Last 7 days: ${data.total_received.toLocaleString()} received, ${data.total_sent.toLocaleString()} sent.`;
  return (
    <Stack gap="md">
      <Card padding="md" radius="md" withBorder>
        <Stack gap="xs">
          <Title order={5}>Weekly summary</Title>
          <Text size="sm" c="dimmed">{totalText}</Text>
          <SimpleGrid cols={4}>
            <Stack gap={0} align="center">
              <Text fw={700}>{data.decisions_made}</Text>
              <Text size="xs" c="dimmed">decisions</Text>
            </Stack>
            <Stack gap={0} align="center">
              <Text fw={700}>{data.promises_fulfilled}</Text>
              <Text size="xs" c="dimmed">promises kept</Text>
            </Stack>
            <Stack gap={0} align="center">
              <Text fw={700}>{data.total_received}</Text>
              <Text size="xs" c="dimmed">received</Text>
            </Stack>
            <Stack gap={0} align="center">
              <Text fw={700}>{data.total_sent}</Text>
              <Text size="xs" c="dimmed">sent</Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
      <Card padding="md" radius="md" withBorder>
        <Title order={6} mb="xs">By tier</Title>
        <SimpleGrid cols={Math.max(1, data.tier_breakdown.length)}>
          {data.tier_breakdown.map((t) => (
            <Stack key={t.classification_tier} gap={0} align="center">
              <Badge color={tierColor(t.classification_tier)} size="lg">{t.classification_tier}</Badge>
              <Text fw={700}>{t.n}</Text>
            </Stack>
          ))}
        </SimpleGrid>
      </Card>
      <Card padding="md" radius="md" withBorder>
        <Title order={6} mb="xs">Top 10 senders</Title>
        <Stack gap={4}>
          {data.top_senders.map((s) => (
            <Group key={s.from_addr} justify="space-between">
              <Text size="sm" lineClamp={1}>{s.from_addr}</Text>
              <Badge variant="light">{s.n}</Badge>
            </Group>
          ))}
        </Stack>
      </Card>
    </Stack>
  );
}

export function TrainingSection(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinTrainingFeedbackList>> | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setBusy(true);
    api.addinTrainingFeedbackList().then(setData).finally(() => setBusy(false));
  }, []);
  if (busy && !data) return <Center p="lg"><Loader /></Center>;
  return (
    <Stack gap="md">
      <Text size="xs" c="dimmed">
        Every time you correct a tier, change a draft, or override the AI, it's logged here. The cascade router uses these as calibration signals over time.
      </Text>
      {(data?.entries ?? []).length === 0 && (
        <Alert color="gray" variant="light">No feedback recorded yet. Use the "Correct tier" buttons in Search to start training.</Alert>
      )}
      <Stack gap="xs">
        {(data?.entries ?? []).map((e) => (
          <Paper key={e.id} p="xs" withBorder>
            <Group justify="space-between" wrap="nowrap">
              <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={500} lineClamp={1}>{e.subject ?? '(no subject)'}</Text>
                <Text size="xs" c="dimmed">{e.from_addr} · {shortDate(e.created_at)}</Text>
              </Stack>
              <Group gap={4}>
                <Badge size="xs" variant="light">{e.correction_kind}</Badge>
                {e.original_tier && e.corrected_tier && (
                  <Text size="xs">
                    <Badge size="xs" color={tierColor(e.original_tier)}>{e.original_tier}</Badge>
                    {' → '}
                    <Badge size="xs" color={tierColor(e.corrected_tier)}>{e.corrected_tier}</Badge>
                  </Text>
                )}
              </Group>
            </Group>
            {e.note && <Text size="xs" c="dimmed" mt={4}>"{e.note}"</Text>}
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
}

export function PersonasSection(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinPersonasList>> | null>(null);
  const [name, setName] = useState('');
  const [tone, setTone] = useState('');
  const [sample, setSample] = useState('');
  const [busy, setBusy] = useState(false);
  async function load() {
    setBusy(true);
    try { setData(await api.addinPersonasList()); } finally { setBusy(false); }
  }
  async function add() {
    if (!name.trim() || !tone.trim()) return;
    await api.addinPersonaCreate(name.trim(), tone.trim(), sample.trim() || undefined);
    setName(''); setTone(''); setSample('');
    await load();
  }
  async function remove(id: string) {
    await api.addinPersonaDelete(id);
    await load();
  }
  useEffect(() => { load(); }, []);

  return (
    <Stack gap="md">
      <Text size="xs" c="dimmed">
        Define named voices ("Rabih executive", "Ops support", "Legal-careful") so drafts can match the situation.
      </Text>
      <Card padding="sm" radius="md" withBorder>
        <Stack gap="xs">
          <Group gap="xs" wrap="nowrap">
            <TextInput size="xs" label="Name" placeholder="e.g. Executive" value={name} onChange={(e) => setName(e.currentTarget.value)} style={{ flex: 1 }} />
            <TextInput size="xs" label="Tone hint" placeholder="e.g. concise, decisive, warm" value={tone} onChange={(e) => setTone(e.currentTarget.value)} style={{ flex: 2 }} />
          </Group>
          <Textarea size="xs" label="Sample text (optional)" placeholder="Paste a paragraph in your voice for the model to mimic"
            autosize minRows={2} maxRows={6} value={sample} onChange={(e) => setSample(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button size="xs" leftSection={<IconPlus {...ICON_PROPS} />} onClick={add} disabled={!name.trim() || !tone.trim()}>
              Save persona
            </Button>
          </Group>
        </Stack>
      </Card>
      {busy && !data ? <Center p="lg"><Loader /></Center> : null}
      <Stack gap="xs">
        {(data?.items ?? []).map((p) => (
          <Paper key={p.id} p="xs" withBorder>
            <Group justify="space-between" wrap="nowrap">
              <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={500}>{p.name}</Text>
                <Text size="xs" c="dimmed">{p.tone_hint}</Text>
              </Stack>
              <ActionIcon variant="subtle" color="red" onClick={() => remove(p.id)}>
                <IconTrash {...ICON_PROPS} />
              </ActionIcon>
            </Group>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
}

export function DndSection(): React.JSX.Element {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.addinDndGet>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setBusy(true);
    api.addinDndGet().then(setData).finally(() => setBusy(false));
  }, []);
  async function save() {
    if (!data) return;
    setSaving(true);
    try {
      await api.addinDndSet({
        quiet_start: data.quiet_start,
        quiet_end: data.quiet_end,
        timezone: data.timezone,
        weekend_silent: data.weekend_silent === 1,
        vip_breaks_dnd: data.vip_breaks_dnd === 1,
      });
      notifications.show({ message: 'Quiet hours saved.' });
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed' });
    } finally { setSaving(false); }
  }
  if (busy && !data) return <Center p="lg"><Loader /></Center>;
  if (!data) return <Alert color="gray" variant="light">Could not load.</Alert>;
  return (
    <Stack gap="md">
      <Text size="xs" c="dimmed">
        Suppress non-urgent push alerts during quiet hours. VIP traffic can still break through if you allow it.
      </Text>
      <Card padding="md" radius="md" withBorder>
        <Stack gap="md">
          <Group gap="xs">
            <TextInput size="xs" label="Quiet from" value={data.quiet_start} type="time"
              onChange={(e) => setData({ ...data, quiet_start: e.currentTarget.value })} />
            <TextInput size="xs" label="Quiet until" value={data.quiet_end} type="time"
              onChange={(e) => setData({ ...data, quiet_end: e.currentTarget.value })} />
            <TextInput size="xs" label="Timezone" value={data.timezone}
              onChange={(e) => setData({ ...data, timezone: e.currentTarget.value })} />
          </Group>
          <Switch checked={data.weekend_silent === 1} onChange={(e) => setData({ ...data, weekend_silent: e.currentTarget.checked ? 1 : 0 })}
            label="Silent on weekends" />
          <Switch checked={data.vip_breaks_dnd === 1} onChange={(e) => setData({ ...data, vip_breaks_dnd: e.currentTarget.checked ? 1 : 0 })}
            label="VIPs can interrupt quiet hours" />
          <Group justify="flex-end">
            <Button size="xs" loading={saving} onClick={save}>Save</Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}

// =========================================================================
// Phase 9 — Inbox Control Score (Round 56)
//
// Replaces the bare A-F grade with a per-bucket breakdown plus an ordered
// "improvement path" — concrete actions ranked by points-recovered, each
// clickable to switch to the relevant tab.
// =========================================================================

// Module-level ring buffer of scores accumulated across sessions (localStorage).
